import React from "react";
import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";
import styled from "styled-components";
import AllowPartner from "../../auth/accessToken/components/AllowPartner";
import useAuth from "../../auth/accessToken/hooks/useAuth";
import AccountSwitcher from "../../common/components/AccountSwitcher";
import useMediaQuery from "../../common/hooks/useMediaQuery";
import {
    defaultRadius,
    distances,
    focusOutlineColor,
    globalMobileBreakpoint,
    palette,
    shadows,
    zIndex,
} from "../../styles/constants";
import MenuButton from "../MenuButton/MenuButton";
import View from "../View";
import profileActive from "./icons/profile-active.svg";
import profileHover from "./icons/profile-hover.svg";
import profileInactive from "./icons/profile-inactive.svg";
import MerchantLogo from "./MerchantLogo";

const iconMap = {
    profile: {
        active: profileActive,
        inactive: profileInactive,
        hover: profileHover,
    },
};

interface AppBarMerchantLogoProps
    extends React.DOMAttributes<HTMLSpanElement>,
        RouteComponentProps<MatchParams> {
    icon: keyof typeof iconMap;
    bg?: string;
    color?: string;
}

interface MatchParams {
    accountId: string;
}

const AppBarMerchantLogo = ({
    match,
    icon,
    bg,
    color,
}: AppBarMerchantLogoProps) => {
    const isMobile = useMediaQuery(`(max-width: ${globalMobileBreakpoint}px)`);
    const history = useHistory();
    const accountUser = useAuth((state) => state.account_user);

    const accountId = match.params.accountId;
    const currentAccount = accountUser.accounts.find(
        (account) => account.account_id === accountId,
    );
    // Test accounts often have no name or icon of their own, so fall back to
    // the paired account (same id, other P/T prefix)
    const pairedAccount = accountUser.accounts.find(
        (account) =>
            account.account_id !== accountId &&
            account.account_id.substring(1) === accountId?.substring(1),
    );
    const getName = (account: typeof currentAccount) =>
        account?.display_name ||
        account?.public_business_name ||
        account?.business_name;
    const name =
        getName(currentAccount) ||
        getName(pairedAccount) ||
        currentAccount?.account_id ||
        "";
    const iconUrl = currentAccount?.icon_url || pairedAccount?.icon_url;

    return (
        <MenuButton
            withinPortal
            position="bottom-left"
            withInert
            listenersIgnoreModals
            bodyStyle={{
                backgroundColor: palette.darkmode[600],
                borderColor: palette.darkmode[300],
                borderRadius: defaultRadius,
                minWidth: "440px",
                maxWidth: "440px",
                zIndex: zIndex.mount_everest,
                boxShadow: shadows.xlarge,
                ...(isMobile && {
                    maxWidth: "100%",
                    minWidth: "100%",
                }),
            }}
            target={(onClick, active) => (
                <AppBarIconWrapper onClick={onClick} data-expanded={active}>
                    <MerchantLogo src={iconUrl} alt={name} />
                    <TextWrapper>
                        <View
                            direction="column"
                            gap="0"
                            justify="flex-start"
                            alignItems="flex-start"
                        >
                            <StyledName>{name}</StyledName>
                            <AllowPartner>
                                <span className="logged-in-label">
                                    {currentAccount?.account_id}
                                </span>
                            </AllowPartner>
                        </View>
                    </TextWrapper>
                    <ArrowUpDownIcon />
                </AppBarIconWrapper>
            )}
        >
            {(toggle) => (
                <AccountSwitcher
                    currentAccount={currentAccount}
                    onChangeAccount={(account) => {
                        history.push(`/${account.account_id}`);
                        toggle();
                    }}
                />
            )}
        </MenuButton>
    );
};

const ArrowUpDownIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        className="abml-arrow-up-down-icon"
    >
        <path
            d="M6 7L10 3L14 7M14 13L10 17L6 13"
            stroke="currentColor"
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const AppBarIconWrapper = styled.button.attrs((props) => ({
    ...props,
    className: "abml-appbar-icon-wrapper",
}))`
    display: flex;
    align-items: center;
    cursor: pointer;
    text-decoration: none;
    border: none;
    background: transparent;
    padding-inline: ${distances.tiny};
    padding-block: ${distances.micro};
    margin-inline: ${distances.small12};
    margin-block: ${distances.small};
    margin-bottom: 0;
    color: ${palette.white};
    border-radius: 8px;
    gap: ${distances.tiny};
    transition: all 0.15s;
    text-align: left;

    outline: 4px solid transparent;
    border-radius: ${defaultRadius};

    span.logged-in-label {
        margin: 0;
        opacity: 0.7;
        font-size: 13px;
    }

    &:hover,
    &:active,
    &:focus, &[data-expanded="true"] {
        background: ${palette.darkmode[800]};
    }

    &:focus-visible {
        outline-color: ${focusOutlineColor};
        outline-offset: 6px;
    }

    @media (max-width: ${globalMobileBreakpoint}px) {
        width: 100%;
        margin: 0;
        justify-content: center;
        align-items: center;
        color: ${palette.neutral[900]};
        &:hover,
        &:active,
        &:focus,
        &[data-expanded="true"] {
            background: ${palette.neutral[200]};
        }
        .abml-arrow-up-down-icon {
            color: ${palette.neutral[900]};
            background: transparent;
        }
    }
`;

const TextWrapper = styled.div.attrs((props) => ({
    ...props,
    className: "abml-text-wrapper",
}))`
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
`;

const StyledName = styled.p`
    margin: 0;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    white-space: wrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    @media (max-width: ${globalMobileBreakpoint}px) {
        max-width: 80px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`;

export default withRouter(AppBarMerchantLogo);
